import React, { useState } from "react";

import {
  AttachFile,
  DeleteOutline,
  DragIndicator,
  EditOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AttachMenu,
  AttachOption,
} from "@/components/alerts/add-v2/media/AttachMenu";
import { entitySummaryLine } from "@/components/alerts/add-v2/model/entityRules";
import { BulletinEntity } from "@/components/alerts/add-v2/model/types";

import { ENTITY_TYPE_ICONS, entityChipLabel } from "./entityTypeMeta";
import { EntityDragHandle } from "./SortableEntity";

/**
 * A collapsed entity: the chip that names it, a one-line summary, the two actions — and the files
 * attached to it.
 *
 * The card is a **drop target**: dragging a media card onto it attaches the file, which is the
 * gesture the design leads with. "Attach media" is the same thing through a menu, for a phone, a
 * keyboard, or a collapsed media section. Both write through `model/mediaLinks`.
 *
 * Opening the entity is `onEdit` — there is no expanded card, because the editor *is* the expanded
 * state, and the attachments are deliberately not repeated inside it.
 */

interface EntityCardProps {
  entity: BulletinEntity;
  /** 1-based within the entity's own type. Derived at render time, never stored. */
  index: number;
  /** Every file on the bulletin, `checked` where it is already attached to this entity. */
  mediaOptions: AttachOption[];
  /**
   * Something required on this entity is still missing. A collapsed card is all there is to see for
   * that entity, so it has to say so — the fields that would carry the errors are not on screen.
   */
  incomplete: boolean;
  /** Wiring from `SortableEntity` — spread onto the handle, not onto the card. */
  dragHandle: EntityDragHandle;
  /** A file is being held over this card, so it is about to be attached. */
  mediaOver: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleMedia: (mediaId: string) => void;
}

export const EntityCard: React.FC<EntityCardProps> = ({
  entity,
  index,
  mediaOptions,
  incomplete,
  dragHandle,
  mediaOver,
  onEdit,
  onDelete,
  onToggleMedia,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const label = entityChipLabel(entity, index, t);
  const attached = mediaOptions.filter((option) => option.checked);

  return (
    <Paper
      variant="outlined"
      // No drop handler: the row is a dnd-kit droppable already (`SortableEntity`), and the media
      // section performs the attach — one place decides what a drop meant (`model/dropIntent.ts`).
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        px: 1.75,
        py: 1.5,
        borderRadius: 2,
        minWidth: 0,
        borderColor: mediaOver
          ? "primary.main"
          : incomplete
            ? "error.main"
            : undefined,
        backgroundColor: mediaOver ? "action.hover" : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {/*
          The handle, not the whole card: the card is already a drop target for media drags, and
          dragging it by its body would fight both that and the text in it being selectable.
        */}
        <IconButton
          size="small"
          ref={dragHandle.setActivatorNodeRef}
          aria-label={t("entities.reorder_entity", { label })}
          {...dragHandle.attributes}
          {...dragHandle.listeners}
          sx={{ flexShrink: 0, cursor: "grab", touchAction: "none" }}
        >
          <DragIndicator fontSize="small" />
        </IconButton>

        <Chip
          size="small"
          icon={ENTITY_TYPE_ICONS[entity.type]}
          label={label}
          sx={{ flexShrink: 0, maxWidth: 260 }}
        />

        {incomplete && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            icon={<WarningAmberOutlined fontSize="small" />}
            label={t("validation.entity_incomplete")}
            onClick={onEdit}
            sx={{ flexShrink: 0 }}
          />
        )}

        {entity.autoAdded && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t("entities.auto_added")}
            sx={{ flexShrink: 0 }}
          />
        )}

        {/* Always present, so it holds the row open even when there is nothing to summarise. */}
        <Typography
          variant="body2"
          title={entitySummaryLine(entity, t)}
          sx={{
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {entitySummaryLine(entity, t)}
        </Typography>

        <IconButton
          size="small"
          aria-label={t("entities.open_entity", { label })}
          onClick={onEdit}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("entities.delete_entity", { label })}
          onClick={onDelete}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        {attached.map((option) => (
          <Chip
            key={option.id}
            size="small"
            icon={option.icon as React.ReactElement}
            label={option.label}
            onDelete={() => onToggleMedia(option.id)}
            sx={{ maxWidth: 200 }}
          />
        ))}

        <Chip
          size="small"
          variant="outlined"
          icon={<AttachFile fontSize="small" />}
          label={t("entities.attach_media")}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ borderStyle: "dashed", color: "text.secondary" }}
        />

        {mediaOver && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
          >
            {t("media.drop_to_attach")}
          </Typography>
        )}
      </Box>

      <AttachMenu
        anchorEl={menuAnchor}
        options={mediaOptions}
        emptyLabel={t("entities.no_media")}
        onToggle={onToggleMedia}
        onClose={() => setMenuAnchor(null)}
      />
    </Paper>
  );
};
